import {
    ImageOpDefinition,
    ImageOpInputsOrNodes,
    EvaluatedImageOpParameters,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {Evaluator} from "@cm/graph-v3"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {determineDescriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/helpers"
import {HalCompositorInfo, ImageOpPerPixelFnHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"
import {
    ImageOpOutputsOfType,
    ImageOpParametersOfType,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ComplexSpecializingImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/complex-specializing-image-op-node"

// This class uses an optimization to merge multiple pixel transforms into a single shader program if possible.
export abstract class ImageOpPerPixelFn<Definition extends ImageOpDefinition> extends ComplexSpecializingImageOpNode<
    Definition,
    ImageOpPerPixelFnHal<Definition>
> {
    protected abstract getCompositingInfo(): HalCompositorInfo

    protected constructor(parameters: ImageOpInputsOrNodes<Definition>) {
        super(parameters)
        this.compositingInfo = this.getCompositingInfo()
    }

    protected requestCompositorRebuild() {
        this.releaseHalOutput()
        this.compositingInfo = this.getCompositingInfo()
    }

    determineDescriptor(parameters: Readonly<Record<string, unknown>>) {
        return determineDescriptor(parameters, this.compositingInfo)
    }

    protected async createImageOpHal(
        evaluator: Evaluator<ImageOpContextHal>,
        evaluatedParameters: EvaluatedImageOpParameters<ImageOpParametersOfType<Definition, "hal">>,
    ): Promise<ImageOpPerPixelFnHal<Definition>> {
        const descriptor = this.determineDescriptor(evaluatedParameters)
        return new ImageOpPerPixelFnHal(evaluator.context, evaluatedParameters, descriptor, this.compositingInfo)
    }

    protected async updateImageOpHal(
        _evaluator: Evaluator<ImageOpContextHal>,
        imageOpHal: ImageOpPerPixelFnHal<Definition>,
        parameters: EvaluatedImageOpParameters<ImageOpParametersOfType<Definition, "hal">>,
    ): Promise<void> {
        const descriptor = this.determineDescriptor(parameters)
        imageOpHal.setParametersAndOutputDescriptor(parameters, descriptor)
    }

    protected getHalResult(
        _evaluator: Evaluator<ImageOpContextHal>,
        imageOpHal: ImageOpPerPixelFnHal<Definition>,
        _evaluatedParameters: EvaluatedImageOpParameters<ImageOpParametersOfType<Definition, "hal">>,
    ): ImageOpOutputsOfType<Definition, "hal"> {
        return imageOpHal as unknown as ImageOpOutputsOfType<Definition, "hal">
    }

    // override async toHal(evaluator: Evaluator<ImageOpContextHal>): Promise<ImageOpOutputsOfType<Definition, "hal">> {
    //     const halOutput = super.toHal(evaluator)
    //     const parameters = await evaluator.getAll(this.halInputs)
    //     const descriptor = this.determineDescriptor(parameters)
    //     if (!this.halOutput) {
    //         this.halOutput = new ImageOpPerPixelFnHal(evaluator.context, parameters, descriptor, this.compositingInfo)
    //         this.halOutput.debugId = this.debugId
    //     } else {
    //         this.halOutput.updateOutputDescriptor(descriptor)
    //         this.halOutput.setParameters(parameters)
    //     }
    //     this.halOutput.addRef()
    //     //@ts-expect-error: ImageOpPerPixelFnHal is a subclass of ImageOpHal, so this is safe (not sure why TS doesn't recognize it)
    //     return this.halOutput
    // }

    private compositingInfo: HalCompositorInfo
}
